import { useEffect, useState } from 'react';

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  onClose: () => void;
}

export default function Toast({ message, type, duration = 5000, onClose }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Entrance animation
    const enterTimer = setTimeout(() => setIsVisible(true), 100);
    
    // Auto-close timer
    const exitTimer = setTimeout(() => {
      handleClose();
    }, duration);

    function handleClose() {
      setIsLeaving(true);
      setTimeout(() => {
        onClose();
      }, 400);
    }

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(exitTimer);
    };
  }, [duration, onClose]);

  const handleManualClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose();
    }, 400);
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          background: 'bg-gradient-to-r from-green-50 to-emerald-50',
          border: 'border-2 border-gradient-to-r from-green-200 to-emerald-200',
          iconBg: 'bg-gradient-to-br from-green-500 to-emerald-600',
          iconColor: 'text-white',
          textColor: 'text-green-800',
          accentColor: 'bg-gradient-to-r from-green-500 to-emerald-600',
          glowColor: 'shadow-amber-200'
        };
      case 'error':
        return {
          background: 'bg-gradient-to-r from-red-50 to-rose-50',
          border: 'border-2 border-gradient-to-r from-red-200 to-rose-200',
          iconBg: 'bg-gradient-to-br from-red-500 to-rose-600',
          iconColor: 'text-white',
          textColor: 'text-red-800',
          accentColor: 'bg-gradient-to-r from-red-500 to-rose-600',
          glowColor: 'shadow-red-200'
        };
      case 'warning':
        return {
          background: 'bg-gradient-to-r from-yellow-50 to-amber-50',
          border: 'border-2 border-gradient-to-r from-yellow-200 to-amber-200',
          iconBg: 'bg-gradient-to-br from-yellow-500 to-amber-600',
          iconColor: 'text-white',
          textColor: 'text-yellow-800',
          accentColor: 'bg-gradient-to-r from-yellow-500 to-amber-600',
          glowColor: 'shadow-yellow-200'
        };
      case 'info':
      default:
        return {
          background: 'bg-gradient-to-r from-brand-50 to-brand-50',
          border: 'border-2 border-gradient-to-r from-brand-200 to-brand-200',
          iconBg: 'bg-gradient-to-br from-brand-500 to-brand-600',
          iconColor: 'text-white',
          textColor: 'text-brand-800',
          accentColor: 'bg-gradient-to-r from-brand-500 to-brand-600',
          glowColor: 'shadow-amber-200'
        };
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const typeStyles = getTypeStyles();

  return (
    <div
      className={`
        relative w-full max-w-none lg:max-w-md ${typeStyles.background}
        rounded-xl lg:rounded-2xl shadow-2xl ${typeStyles.glowColor} border border-gray-200 lg:border-2
        backdrop-blur-lg transform transition-all duration-400 ease-out
        ${isVisible && !isLeaving
          ? 'translate-x-0 translate-y-0 opacity-100 scale-100 rotate-0'
          : isLeaving
          ? '-translate-y-3 lg:translate-y-0 lg:translate-x-full opacity-0 scale-95 rotate-0 lg:rotate-2'
          : '-translate-y-3 lg:translate-y-0 lg:-translate-x-full opacity-0 scale-95 rotate-0 lg:-rotate-2'
        }
        hover:shadow-3xl hover:scale-[1.02] hover:-translate-y-1
        before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/20 before:to-transparent 
        before:rounded-2xl before:pointer-events-none
      `}
      style={{
        boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.3) inset`,
      }}
    >
      {/* Premium glass overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl pointer-events-none" />
      
      <div className="relative p-3 lg:p-5">
        <div className="flex items-start">
          {/* Premium Icon */}
          <div className={`
            flex-shrink-0 ${typeStyles.iconBg} ${typeStyles.iconColor}
            rounded-lg lg:rounded-xl p-2 mr-3 lg:p-3 lg:mr-4 shadow-lg lg:shadow-xl ring-1 lg:ring-2 ring-white/20
            transform transition-all duration-300 hover:scale-110 hover:rotate-6
          `}>
            <div className="relative">
              {getIcon()}
              {/* Icon glow effect */}
              <div className="absolute inset-0 bg-white/20 rounded-lg blur-md" />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0 pt-0.5 lg:pt-1.5">
            <p className={`text-xs lg:text-sm font-semibold ${typeStyles.textColor} leading-snug lg:leading-relaxed break-words pr-2`}>
              {message}
            </p>
          </div>

          {/* Premium Close Button */}
          <button
            onClick={handleManualClose}
            className="
              flex-shrink-0 ml-2 lg:ml-3 bg-white/80 hover:bg-white
              rounded-lg lg:rounded-xl p-1.5 lg:p-2 text-gray-500 hover:text-gray-700
              shadow-md lg:shadow-lg hover:shadow-xl
              transition-all duration-300 hover:scale-110 hover:rotate-90
              focus:outline-none focus:ring-2 focus:ring-gray-300
              backdrop-blur-sm border border-white/50
            "
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Premium Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 lg:h-2 bg-black/10 rounded-b-xl lg:rounded-b-2xl overflow-hidden backdrop-blur-sm">
        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-white/10" />
        <div 
          className={`h-full ${typeStyles.accentColor} rounded-b-2xl shadow-lg relative overflow-hidden`}
          style={{
            animation: `shrinkWidth ${duration}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
          }}
        >
          {/* Progress bar shine effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
        </div>
      </div>
      
      {/* Particle effects for premium feel */}
      <div className="absolute -top-1 -right-1 w-3 h-3 bg-white/60 rounded-full blur-sm animate-pulse" />
      <div className="absolute top-2 -left-1 w-2 h-2 bg-white/40 rounded-full blur-sm animate-pulse delay-300" />
      
      <style>{`
        @keyframes shrinkWidth {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
        
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        .shadow-3xl {
          box-shadow: 0 35px 60px -12px rgba(0, 0, 0, 0.35);
        }
      `}</style>
    </div>
  );
}